import { LoadingButton } from "@mui/lab";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { Form, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import SelectComponent from "../FormComponents/DefaultSelect/SelectComponent";
import { PAGES } from "./CreateBookingWrapper";
import Calendar from "./Calendar";
import TextareaComponent from "../FormComponents/Textarea/TextareaComponent";
import {
  createBookingAsync,
  createBookingFormState,
  currentPage,
  selectedDate,
  selectedTime,
  setDisplayBookings,
  setPage,
  updateCreateBookingFormState,
  branches,
  regions,
  reasons,
  reasonCategories,
  bookingTypes,
  branchesWithRegions,
  availableReasons,
  activeReasons,
  bookingTaxpayerID,
  getAllAvailableDates,
  bookingCreatedSuccessfully,
  requiredFiles,
  setCreateBookingSubmissionStarted,
  createBookingSubmissionStarted,
  bookingErrors,
  setDates,
  setDocumentsStillRequired,
  filteredAvailableReasons,
  taxpayerRegion,
  contactCenterNumber,
  setSelectedTime,
  setSelectedDate,
  setTimes,
  availableDatesLoading,
} from "../../features/BookingsSlice";
import { FormApi, FormState } from "final-form";
import { useCallback, useEffect, useState } from "react";
import { DropDownOption } from "../../models/DropDownOption";
import { Branch } from "../../models/Branches";
import {
  ActiveReasons, AvailableBranchReasons, AvailableBranches,
} from "../../models/BookingReasons";
import MenuItemsForDropdown from '../FormComponents/MenuItemsForDropdown/MenuItemsForDropdown';
import { isAgent } from "../../features/CaptureDetailsSlice";
import { DisabledSelectOptions, filterReasonCategoriesByBranch, formOptionsAvailabilityCheck, setAllDisabledBranchOptions, setAllDisabledRegionOptions, setDisabledBookingTypeOptions, setDisabledBranchOptions, setDisabledCategoryOptions, setDisabledReasonForCategoryOptions, setDisabledReasonOptions, setDisabledRegionOptions } from "./FilteringHelper";
import { BRANCH_BOOKING_TYPES, NO_REGION, SUB_BRANCH_TYPES, WALK_IN_TYPES } from "../../features/Constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const CreateBooking = () => {
  const dispatch = useDispatch();
  const pageState = useSelector(currentPage);
  const bookingDetailsForm = useSelector(createBookingFormState);
  const selectedBookingTime = useSelector(selectedTime);
  const selectedBookingDate = useSelector(selectedDate);
  const taxPayer = useSelector(bookingTaxpayerID);
  const bookingCreated = useSelector(bookingCreatedSuccessfully);
  const hasCreateBookingStarted = useSelector(createBookingSubmissionStarted);
  const bookingErrorState = useSelector(bookingErrors);
  const agent = useSelector(isAgent);
  const region = useSelector(taxpayerRegion);
  const contactNumber = useSelector(contactCenterNumber);
  const datesLoading = useSelector(availableDatesLoading);

  const branchesOptions = useSelector(branches);
  const regionsOptions = useSelector(regions);
  const reasonsOptions = useSelector(reasons);
  const reasonCategoryOptions = useSelector(reasonCategories);
  const bookingTypeOptions = useSelector(bookingTypes);
  const allBranches = useSelector(branchesWithRegions);
  const reasonsAvailable = useSelector(availableReasons);
  const filteredReasonsAvailable = useSelector(filteredAvailableReasons);
  const allActiveReasons = useSelector(activeReasons);

  const requiredUploadFiles = useSelector(requiredFiles);

  const initForm = {
    reason: null,
    bookingType: null,
    branch: null,
  };

  const prefilledState = bookingDetailsForm?.values ?? initForm;

  const [filteredBranches, setFilteredBranches] = useState(branchesOptions);
  const [filteredReasons, setFilteredReasons] = useState(reasonsOptions);
  const [filteredReasonCategories, setFilteredReasonCategories] = useState(reasonCategoryOptions);
  const [filteredBookingTypes, setFilteredBookingTypes] = useState(bookingTypeOptions);
  const [filteredRegions, setFilteredRegions] = useState(regionsOptions);
  const [disabledOptions, setDisabledOptions] = useState<DisabledSelectOptions | null>(null);
  const [disabledFilteredOptions, setDisabledFilteredOptions] = useState<DisabledSelectOptions | null>(null);
  const [originalFilterOptions, setOriginalFilterOptions] = useState<DisabledSelectOptions | null>(null);

  const triggerFiltering = useCallback((): void => {    
    if(allBranches.length > 0 && bookingTypeOptions.length > 0 && allActiveReasons.length > 0) {
      const optionsForAllAvailableReasons = formOptionsAvailabilityCheck(allActiveReasons, reasonsAvailable, allBranches);
      const optionsForFilteredAvailableReasons = formOptionsAvailabilityCheck(allActiveReasons, filteredReasonsAvailable, allBranches);
      setDisabledOptions(optionsForAllAvailableReasons);
      setDisabledFilteredOptions(optionsForFilteredAvailableReasons);
      setOriginalFilterOptions(optionsForAllAvailableReasons);
  
      const disabledBookingTypes = setDisabledBookingTypeOptions(bookingTypeOptions, optionsForAllAvailableReasons.bookingTypes, optionsForFilteredAvailableReasons.bookingTypes, region);    
      setFilteredBookingTypes(disabledBookingTypes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allActiveReasons, allBranches, bookingTypeOptions, reasonsAvailable, filteredReasonsAvailable, region]);

  useEffect(() => {
    triggerFiltering();
    if(bookingCreated) {
      dispatch(setDisplayBookings(false));
      if(requiredUploadFiles.length > 0 && !agent) {
        dispatch(setPage(PAGES.UploadSupportingDocs));
      } else {
        dispatch(setDocumentsStillRequired(false));
        dispatch(setPage(PAGES.Confirmation));
      }
    }
  }, [triggerFiltering, dispatch, requiredUploadFiles, bookingCreated, agent])

  const back = () => {
    dispatch(updateCreateBookingFormState(null));
    dispatch(setDisplayBookings(true));
    dispatch(setDates([]));
  };

  const filterCategoriesBasedOnBookingType = (type: string) => {
    const filteredActiveReasons = allActiveReasons.filter((reason: ActiveReasons) => reason.ChannelType === type);
    const disabledCategories = setDisabledCategoryOptions(filteredActiveReasons, reasonsAvailable, reasonCategoryOptions, disabledOptions, allActiveReasons, type, reasonsOptions);
    setFilteredReasonCategories(disabledCategories);
  }

  const filterReasonsBasedOnCategory = (category: string, type: string, branch: string) => {
    const filteredActiveReasons = allActiveReasons.filter((reason: ActiveReasons) => reason.ReasonCategory === category && reason.ChannelType === type);
    const disabledReasons = setDisabledReasonForCategoryOptions(filteredActiveReasons, reasonsAvailable, branch);
    setFilteredReasons(disabledReasons);
  };

  const filterBranchesBasedOnRegion = (region: string) => {
    if (region === "") {
      setFilteredBranches(branchesOptions);
      return;
    }

    const newBranches: DropDownOption[] = allBranches.filter((branch: Branch) => branch.BranchRegion === region)
    .map((branch: Branch) => {
      return {
        set: "branches",
        seq: 1,
        description: branch.BranchName,
        value: branch.BranchCode,
        disabled: false,
      };
    });

    const reasonIDs = allActiveReasons.filter((reason: ActiveReasons) => reason.ChannelType === bookingDetailsForm?.values?.bookingType).map((reason: ActiveReasons) => reason.ReasonPermutationID);
    const availableBranchIDs = reasonsAvailable.filter((reason: AvailableBranchReasons) => reasonIDs.includes(reason.ReasonPermutationID)).flatMap((reason: AvailableBranchReasons) => reason.Branches).map((avail: AvailableBranches) => avail.BranchCode);
    const availableBranches = newBranches.filter((branch: DropDownOption) => availableBranchIDs.includes(branch.value));

    if(BRANCH_BOOKING_TYPES.includes(bookingDetailsForm?.values?.bookingType?.toLowerCase()))
    {      
      const disabledRegions = setAllDisabledBranchOptions(disabledOptions?.reasonBranches ?? [], availableBranches, bookingDetailsForm?.values?.bookingType, reasonsAvailable);
      setFilteredBranches(disabledRegions);
      return;
    }

    const disabledBranchRegionOptions = setDisabledBranchOptions(reasonsAvailable, availableBranches, Number(bookingDetailsForm?.values.reason));
    setFilteredBranches(disabledBranchRegionOptions);
  };

  const clearFormValues = (form: FormApi<Record<string, any>, Partial<Record<string, any>>>) => {
    form.change('reasonCategory', '');
    form.change('reason', '');
    form.change('region', '');
    form.change('branch', '');
    form.change('subBranch', '');
  }

  const getAvailableDates = (reasonID: string, branch: string | null) => {
    const data = {
      date: selectedBookingDate ? new Date(selectedBookingDate).toISOString() : new Date().toISOString(),
      bookingTaxpayerID: taxPayer,
      branchCode: branch,
      reasonPermutationId: reasonID
    };
    dispatch(setDates([]));
    dispatch(getAllAvailableDates(data));
  }

  const resetSelectedDateAndTime = () => {
    dispatch(setSelectedDate(''));
    dispatch(setSelectedTime(''));
    dispatch(setDates([]));
    dispatch(setTimes({ morning: [], afternoon: [] }));
  }

  return (
    <>
      {!region || region === "" || region === NO_REGION
        ? <Box sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#E3EFF57D",
            borderRadius: "8px",
            justifyContent: "space-between",
            mt: "12px",
            mb: "12px",
            p: "8px",
          }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: "16px" }} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography color="primary">
                <strong>Please Note:</strong>
              </Typography>
              <Typography color="primary" sx={{ fontSize: "14px" }}>                
                There are errors in your registration data. Please make a Walk-In, Mobile Tax Unit or Pop-Up Service booking or alternately contact SARS on <strong>{contactNumber}</strong> for an eBooking. We apologise for the inconvenience.
              </Typography>
            </Box>
          </Box>
        : null
      }

      <Form
        onSubmit={() => {
          dispatch(setCreateBookingSubmissionStarted(true));
          dispatch(createBookingAsync());
        }}
        initialValues={prefilledState}
      >
        {({ handleSubmit, submitting, pristine, values, form, errors }) => (
          <>
            <Box sx={{display: "flex", flexDirection: "column", justifyContent: 'space-between', height: '70%'}}>
              <FormSpy
                onChange={(
                  state: FormState<
                    Record<string, any>,
                    Partial<Record<string, any>>
                  >
                ) => {
                  if (state?.dirty && Object.keys(state.modified ?? {}).length > 0) {
                    dispatch(updateCreateBookingFormState(state));

                    if (state?.values?.bookingType && state?.dirtyFields?.bookingType) {
                      clearFormValues(form);
                      dispatch(setDates([]));

                      let options = {...disabledOptions};
                      if(BRANCH_BOOKING_TYPES.includes(state?.values?.bookingType?.toLowerCase())) {
                        setDisabledOptions(originalFilterOptions);
                        options = {...originalFilterOptions}
                      } else {
                        setDisabledOptions(disabledFilteredOptions);
                        options = {...disabledFilteredOptions}
                      }

                      const disabledRegions = setAllDisabledRegionOptions(options?.reasonBranches ?? [], regionsOptions, state?.values?.bookingType);
                      setFilteredRegions(disabledRegions);
                      filterCategoriesBasedOnBookingType(state?.values?.bookingType);

                      resetSelectedDateAndTime();
                    }

                    if (state?.values?.reasonCategory && state?.dirtyFields?.reasonCategory) {
                      form.change('reason', '');
                      resetSelectedDateAndTime();
                      filterReasonsBasedOnCategory(state?.values?.reasonCategory, state?.values?.bookingType, state?.values?.branch);
                    }

                    if (state?.values?.region && state?.dirtyFields?.region) {
                      form.change('branch', '');
                      resetSelectedDateAndTime();
                      filterBranchesBasedOnRegion(state?.values?.region);
                    }

                    if (state?.values?.branch && state?.dirtyFields?.branch && (!state?.values?.reason || state?.values?.reason === "")) {
                      const categories = filterReasonCategoriesByBranch(state?.values?.branch, reasonsAvailable, allActiveReasons, state?.values?.bookingType);
                      setFilteredReasonCategories(categories);
                    }

                    if((state?.values?.reason && state?.dirtyFields?.reason)) {
                      const disabledRegions = setDisabledRegionOptions(disabledOptions?.reasonBranches ?? [], state?.values?.reason, regionsOptions);
                      setFilteredRegions(disabledRegions);
                    }

                    if(state?.values?.reason && state?.dirtyFields?.reason && state?.values?.reason !== "" && !BRANCH_BOOKING_TYPES.includes(state?.values?.bookingType?.toLowerCase())) {
                      resetSelectedDateAndTime();
                      getAvailableDates(state?.values?.reason, null);
                    }

                    if (state?.values?.branch && state?.values?.branch !== "" && state?.values?.reason && state?.values?.reason !== "" && (state?.dirtyFields?.reason || state?.dirtyFields?.branch)) {
                      resetSelectedDateAndTime();
                      getAvailableDates(state?.values?.reason, state?.values?.branch);
                    }
                  }
                }}
              />

              <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
                  <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                    flex: "0 0 100%",
                  }})}>
                    <SelectComponent
                      id="booking-type"
                      label="Booking Type"
                      name="bookingType"
                      required={true}
                      options={MenuItemsForDropdown(
                        "bookingTypes",
                        filteredBookingTypes,
                      )}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}> 
                  {BRANCH_BOOKING_TYPES.includes(bookingDetailsForm?.values?.bookingType?.toLowerCase()) && (
                    <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                      flex: "0 0 100%",
                    }})}>
                      <SelectComponent
                        id="region"
                        label="Province"
                        name="region"
                        required={true}
                        options={MenuItemsForDropdown(
                          "regions",
                          filteredRegions,
                        )}
                      />
                    </Box>
                  )}

                  {WALK_IN_TYPES.includes(bookingDetailsForm?.values?.bookingType?.toLowerCase()) && (
                    <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                      flex: "0 0 100%",
                    }})}>
                      <SelectComponent
                        id="branch"
                        label="Branch"
                        name="branch"
                        required={true}
                        options={MenuItemsForDropdown(
                          "branches",
                          filteredBranches,
                        )}
                      />
                    </Box>
                  )}

                  {SUB_BRANCH_TYPES.includes(bookingDetailsForm?.values?.bookingType?.toLowerCase()) && (
                    <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                      flex: "0 0 100%",
                    }})}>
                      <SelectComponent
                        id="sub-branches"
                        label="Branches"
                        name="branch"
                        required={true}
                        options={MenuItemsForDropdown(
                          "branches",
                          filteredBranches,
                        )}
                      />
                    </Box>
                  )}

                  <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                    flex: "0 0 100%",
                  }})}>
                    <SelectComponent
                      id="reason-category"
                      label="Reason Category"
                      name="reasonCategory"
                      required={true}
                      options={MenuItemsForDropdown(
                        "reasonCategories",
                        filteredReasonCategories,
                      )}
                    />
                  </Box>

                  <Box sx={(theme) => ({ flex: "0 0 49%", [theme.breakpoints.down('sm')]: {
                    flex: "0 0 100%",
                  }})}>
                    <SelectComponent
                      id="reason"
                      label="Reason"
                      name="reason"
                      required={true}
                      options={MenuItemsForDropdown(
                        "reasons",
                        filteredReasons,
                      )}
                    />
                  </Box>

                  <Box
                    sx={{ flex: "0 0 100%"}}
                  >
                    <TextareaComponent
                      id="reason-summary"
                      label="Reason Summary (optional)"
                      name="reasonSummary"
                      required={false}
                      rows={2}
                    />
                    <Typography sx={{ fontSize: "10px", ml: "8px" }}>
                      Briefly explain the reason for your appointment
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: "22px" }}>
                  <Calendar />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <LoadingButton
                  id="create-booking-back"
                  variant="outlined"
                  color="primary"
                  onClick={back}
                  sx={{ mb: "12px" }}
                >
                  Back
                </LoadingButton>

                <LoadingButton
                  id="continue-to-upload"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  loading={submitting}
                  sx={{ mb: "12px" }}
                  disabled={
                    !bookingDetailsForm?.valid ||
                    selectedBookingTime === "" ||
                    selectedBookingDate === ""
                  }
                >
                  {pageState === PAGES.Confirmation ? "Done" : "Next"}
                </LoadingButton>
              </Box>
            </Box>
          </>
        )}
      </Form>

      <Backdrop
        sx={{ color: '#fff', zIndex: 200, flexDirection: 'column' }}
        open={datesLoading}
      >
        <CircularProgress color="inherit" size="4rem" thickness={4} />
        <Typography color="white" sx={{ mt: '1rem', fontSize: '18px' }}>Loading Dates ...</Typography>
      </Backdrop>

      <Backdrop
        sx={{ color: '#fff', zIndex: 200, flexDirection: 'column' }}
        open={hasCreateBookingStarted && (bookingErrorState === null || bookingErrorState.error === null)}
      >
        <CircularProgress color="inherit" size="4rem" thickness={4} />
        <Typography color="white" sx={{ mt: '1rem', fontSize: '18px' }}>Creating Booking ...</Typography>
      </Backdrop>
    </>
  );
};

export default CreateBooking;
